import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { catchHttpError } from '../utils/catch-http-error';
import {
    CreateCustomTemplateAction,
    CreateCustomTemplateFailureAction,
    CreateCustomTemplateSuccessAction,
    CustomTemplatesActionTypes,
    DeleteCustomTemplateAction,
    DeleteCustomTemplateFailureAction,
    DeleteCustomTemplateSuccessAction,
    LoadCustomTemplateAction,
    LoadCustomTemplateFailureAction,
    LoadCustomTemplatesAction,
    LoadCustomTemplatesFailureAction,
    LoadCustomTemplatesSuccessAction,
    LoadCustomTemplateSuccessAction,
    UpdateCustomTemplateAction,
    UpdateCustomTemplateFailureAction,
    UpdateCustomTemplateSuccessAction
} from './actions';
import { CustomTemplatesService } from './service';

@Injectable()
export class CustomTemplatesEffects {
    private actions$ = inject(Actions);
    private service = inject(CustomTemplatesService);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    loadCustomTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LoadCustomTemplatesAction>(CustomTemplatesActionTypes.LOAD_CUSTOM_TEMPLATES),
            switchMap(action =>
                this.service.getCustomTemplates(action.payload.type).pipe(
                    map(customTemplates => new LoadCustomTemplatesSuccessAction(customTemplates)),
                    catchHttpError(error => of(new LoadCustomTemplatesFailureAction(error)))
                )
            )
        )
    );

    loadCustomTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LoadCustomTemplateAction>(CustomTemplatesActionTypes.LOAD_CUSTOM_TEMPLATE),
            switchMap(action =>
                this.service.getCustomTemplate(action.payload).pipe(
                    map(customTemplate => new LoadCustomTemplateSuccessAction(customTemplate)),
                    catchHttpError(error => of(new LoadCustomTemplateFailureAction(error)))
                )
            )
        )
    );

    createCustomTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateCustomTemplateAction>(CustomTemplatesActionTypes.CREATE_CUSTOM_TEMPLATE),
            switchMap(action =>
                this.service.createCustomTemplate(action.payload).pipe(
                    map(customTemplate => new CreateCustomTemplateSuccessAction(customTemplate)),
                    catchHttpError(error => of(new CreateCustomTemplateFailureAction(error)))
                )
            )
        )
    );

    updateCustomTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateCustomTemplateAction>(CustomTemplatesActionTypes.UPDATE_CUSTOM_TEMPLATE),
            switchMap(action =>
                this.service.updateCustomTemplate(action.payload).pipe(
                    map(customTemplate => new UpdateCustomTemplateSuccessAction(customTemplate)),
                    catchHttpError(error => of(new UpdateCustomTemplateFailureAction(error)))
                )
            )
        )
    );

    deleteCustomTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DeleteCustomTemplateAction>(CustomTemplatesActionTypes.DELETE_CUSTOM_TEMPLATE),
            switchMap(action =>
                this.service.deleteCustomTemplate(action.payload).pipe(
                    map(customTemplate => new DeleteCustomTemplateSuccessAction()),
                    catchHttpError(error => of(new DeleteCustomTemplateFailureAction(error)))
                )
            )
        )
    );
}
